import { useMemo } from 'react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip } from 'recharts';
import { getSkillScore } from '@/utils/techDevCalculator';

/**
 * Development Radar Chart Component
 * 
 * Displays an 8-axis radar chart showing athlete development profile.
 * Uses existing Technical Development skill evaluations.
 * 
 * RADAR AXES (8):
 * TECHNICAL:
 *   - Ball Control = avg(ball_control + first_touch)
 *   - Passing = passing
 *   - Dribbling = dribbling
 *   - Finishing = avg(finishing + shooting)
 * 
 * PHYSICAL:
 *   - Speed & Agility = avg(speed + acceleration + agility + explosiveness)
 *   - Balance & Coordination = avg(balance + coordination)
 * 
 * TACTICAL / IQ:
 *   - Decision Making = avg(decision_making + reaction_time)
 *   - Game Awareness = avg(vision + positioning + game_awareness)
 * 
 * Meybra V3 Scoring: Focus=4.0-6.0, Developing=6.1-8.0, Proficient=8.1-10.0
 */

// Radar axis definitions with skill mappings
const RADAR_AXES = [
  {
    key: 'ballControl',
    label: 'Ball Control',
    category: 'technical',
    skills: ['ball_control', 'first_touch']
  },
  {
    key: 'passing',
    label: 'Passing',
    category: 'technical',
    skills: ['passing']
  },
  {
    key: 'dribbling',
    label: 'Dribbling',
    category: 'technical',
    skills: ['dribbling']
  },
  {
    key: 'finishing',
    label: 'Finishing',
    category: 'technical',
    skills: ['finishing', 'shooting']
  },
  {
    key: 'speedAgility',
    label: 'Speed & Agility',
    category: 'physical',
    skills: ['speed', 'acceleration', 'agility', 'explosiveness']
  },
  {
    key: 'balanceCoord',
    label: 'Balance & Coord',
    category: 'physical',
    skills: ['balance', 'coordination']
  },
  {
    key: 'decisionMaking',
    label: 'Decision Making',
    category: 'tactical',
    skills: ['decision_making', 'reaction_time']
  },
  {
    key: 'gameAwareness',
    label: 'Game Awareness',
    category: 'tactical',
    skills: ['vision', 'positioning', 'game_awareness']
  }
];

// Category colors
const CATEGORY_COLORS = {
  technical: '#10B981', // emerald
  physical: '#3B82F6',  // blue
  tactical: '#EAB308'   // yellow
};

/**
 * Calculate average score for a set of skills
 * Uses manual scores when available, otherwise uses status-based defaults
 */
const calculateAxisScore = (techDev, skills) => {
  if (!techDev) return 0;
  
  let total = 0;
  let count = 0;
  
  skills.forEach(skillKey => {
    const skillData = techDev[skillKey];
    if (skillData?.status) {
      const score = getSkillScore(skillData);
      if (score > 0) {
        total += score;
        count++;
      }
    }
  });
  
  return count > 0 ? Math.round((total / count) * 10) / 10 : 0;
};

/**
 * Custom tooltip for radar chart
 */
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-card/95 backdrop-blur-sm border border-border px-3 py-2 rounded-sm shadow-lg">
        <p className="font-medium text-sm text-foreground">{data.label}</p>
        <p className="text-primary font-heading text-lg">{data.value}/10</p>
        <p className="text-xs text-muted-foreground capitalize">{data.category}</p>
      </div>
    );
  }
  return null;
};

const DevelopmentRadarChart = ({ techDev, showTitle = true, height = 350 }) => {
  // Calculate radar data from tech dev evaluations
  const radarData = useMemo(() => {
    if (!techDev) return [];
    
    return RADAR_AXES.map(axis => ({
      ...axis,
      value: calculateAxisScore(techDev, axis.skills),
      fullMark: 10
    }));
  }, [techDev]);
  
  // Check if we have any data to display
  const hasData = useMemo(() => {
    return radarData.some(d => d.value > 0);
  }, [radarData]);
  
  // Calculate overall score
  const overallScore = useMemo(() => {
    const validScores = radarData.filter(d => d.value > 0);
    if (validScores.length === 0) return 0;
    const sum = validScores.reduce((acc, d) => acc + d.value, 0);
    return Math.round((sum / validScores.length) * 10) / 10;
  }, [radarData]);

  // Don't render if no evaluation data
  if (!hasData) {
    return null;
  }

  return (
    <div className="bg-card border border-border/40 rounded-sm p-4 mb-6" data-testid="development-radar-chart">
      {showTitle && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg uppercase tracking-wider flex items-center gap-2">
            <span className="text-primary">◉</span>
            Development Profile
          </h3>
        </div>
      )}
      
      <div className="flex flex-col lg:flex-row items-center gap-4">
        {/* Radar Chart */}
        <div className="w-full lg:w-2/3" style={{ height }}>
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart cx="50%" cy="50%" outerRadius="75%" data={radarData}>
              <PolarGrid 
                stroke="rgba(255,255,255,0.1)" 
                strokeDasharray="3 3"
              />
              <PolarAngleAxis 
                dataKey="label" 
                tick={{ fill: '#888', fontSize: 11 }}
                tickLine={false}
              />
              <PolarRadiusAxis 
                angle={22.5} 
                domain={[0, 10]} 
                tick={{ fill: '#666', fontSize: 10 }}
                tickCount={6}
                axisLine={false}
              />
              <Radar
                name="Development"
                dataKey="value"
                stroke="#D4AF37"
                strokeWidth={2}
                fill="#D4AF37"
                fillOpacity={0.25}
                animationDuration={800}
                animationEasing="ease-out"
              />
              <Tooltip content={<CustomTooltip />} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
        
        {/* Legend */}
        <div className="w-full lg:w-1/3 space-y-2">
          <div className="text-xs uppercase text-muted-foreground tracking-wider mb-3">Axis Scores</div>
          {radarData.map(axis => (
            <div key={axis.key} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <div 
                  className="w-2 h-2 rounded-full" 
                  style={{ backgroundColor: CATEGORY_COLORS[axis.category] }}
                />
                <span className="text-muted-foreground">{axis.label}</span>
              </div>
              <span className={`font-medium ${axis.value >= 8.1 ? 'text-green-400' : axis.value >= 6.1 ? 'text-yellow-400' : 'text-muted-foreground'}`}>
                {axis.value > 0 ? axis.value : '-'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DevelopmentRadarChart;
